import { ControlledPopover } from '@round/ui-kit';
import { buildFetchOptions, SongOption, useSongSelect } from 'Modules/Plans/Songs/hooks/useSongSelect';
import { useEffect, useMemo, useState } from 'react';
import styles from './ProjectSongDropdown.module.css';
import { Select, assets } from '@round/whitelabel-ui';
import useOnClickOutside from 'Hooks/useOnClickOutside';
import { StylesConfig } from 'react-select';
import { Button } from '@round/whitelabel-ui';
import SongBadge from 'Modules/Plans/Songs/components/SongBadge/SongBadge';
import { showNotification } from 'helpers';
import useProjectId from '../../hooks/useProjectId';
import { useUpdateProjectMutation } from '@round/creatorbase-helpers/api-hooks';
import useProjectDetails from '../../useProjectDetails';

const { Plus: PlusIcon } = assets;

type Props = {
    songId: number | null | undefined;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCreateSong: () => void;
    anchor: HTMLElement | null;
};

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        margin: '0 0.5rem',
    }),
    menu: (base) => ({
        ...base,
        boxShadow: 'none',
        position: 'static',
        border: 'none',
        borderTop: '1px solid #D1D5DB',
        borderBottom: '1px solid #D1D5DB',
        borderRadius: '0',
        width: '100%',
    }),
};

const ProjectSongDropdown = ({ songId, isOpen, onOpenChange, anchor, onCreateSong }: Props) => {
    const projectId = useProjectId();
    const { team } = useProjectDetails();
    const { update: updateProject, isLoading: isUpdateLoading } = useUpdateProjectMutation();
    const [popoutRef, setPopoutRef] = useState<HTMLDivElement | null>(null);

    const {
        props: songSelectProps,
        resetValue,
        resetOptions,
    } = useSongSelect({
        initOn: 'mount',
        initialValueId: songId || undefined,
        // show only songs for current project's client
        fetchOptions: useMemo(() => buildFetchOptions({ client_id: team?.client_id }), [team?.client_id]),
    });

    useEffect(() => {
        if (team?.client_id) {
            resetOptions();
        }
    }, [resetOptions, team?.client_id]);

    const handleUpdateProjectSong = async () => {
        if (!projectId) {
            return;
        }

        try {
            await updateProject({
                id: projectId,
                song_id: (songSelectProps.value as SongOption | null)?.value || null,
            });
            showNotification('Song updated', 'info');
            onOpenChange(false);
        } catch {
            showNotification('Failed to update song', 'error');
        }
    };

    const handleCloseWithoutSaving = () => {
        onOpenChange(false);
        if (songId !== (songSelectProps.value as SongOption | null)?.value) {
            resetValue();
        }
    };

    useOnClickOutside(popoutRef, () => handleCloseWithoutSaving());

    const linkOrUpdate = !!songId ? 'Update' : 'Link';

    return (
        <ControlledPopover
            anchorElement={anchor}
            isOpen={isOpen}
            options={{ placement: 'bottom', modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}
            overlayClassName={styles.overlay}
        >
            <div className={styles.container} ref={setPopoutRef}>
                <div className={styles.selectContainer}>
                    <label htmlFor="postUrl" className={styles.label}>
                        {linkOrUpdate} Song
                    </label>
                    <Select
                        {...songSelectProps}
                        styles={selectStyles}
                        menuIsOpen={!songSelectProps.value || !!songSelectProps.inputValue}
                        placeholder="Search song"
                        isClearable
                        isSearchable
                        menuPortalTarget={null}
                        menuPlacement="bottom"
                        components={{ DropdownIndicator: null }}
                        formatOptionLabel={(option, meta) =>
                            meta.context === 'menu' ? (
                                <SongBadge
                                    title={option.label}
                                    artistName={option.artistName}
                                    imageUrl={option.image}
                                    direction="column"
                                />
                            ) : (
                                option.label
                            )
                        }
                        isLoading={isUpdateLoading || songSelectProps.isLoading}
                        isDisabled={isUpdateLoading}
                    />
                </div>
                <div className={styles.actions}>
                    <Button
                        appearance="primary"
                        disabled={(songSelectProps.value as SongOption | null)?.value === songId}
                        onClick={() => handleUpdateProjectSong()}
                    >
                        Save
                    </Button>

                    <Button
                        appearance="ghost"
                        onClick={() => {
                            onCreateSong();
                            handleCloseWithoutSaving();
                        }}
                    >
                        <PlusIcon /> New
                    </Button>
                </div>
            </div>
        </ControlledPopover>
    );
};

export default ProjectSongDropdown;
